/**
 * The shape of a car, stated once (SPEC-002).
 *
 * The catalogue is authored as one JSON document per language plus one shared
 * facts file, both under `/assets/content/cars/`, and fetched at runtime rather
 * than bundled. This module is what BINDS those files: the vocabularies below
 * are the closed sets a content pack may draw from, and `carCatalogue.js`
 * refuses a record that steps outside them.
 *
 * Closed rather than free text because every one of these values reaches a
 * message key (`cars.class.supercar`), a design token (`--livery-red`) or a
 * filter. A pack that invents `"class": "hypercar"` would render the raw key on
 * screen and silently drop out of every filter, and nothing would fail.
 */

/** The decade a car BELONGS to, which is not always the decade it launched. */
export const DECADES = ['80s', '90s', '00s'];

/**
 * What kind of thing it is. Five, and deliberately not more: these are the
 * distinctions a 90s bedroom-poster wall actually made.
 */
export const CLASSES = [
    'supercar',
    'grand-tourer',
    'jdm-icon',
    'homologation',
    'roadster',
    // The LM002, and for now only the LM002 (SPEC-016 D-14). A sixth class held
    // by one car is a weaker argument than the five above it, and it is still
    // the right trade: filed as `supercar` that car would sit in a filter beside
    // the F40 and the McLaren, and the honest five would have been broken to
    // avoid admitting a sixth. A class with one member is a small cost; a class
    // that lies is not.
    'off-roader',
];

/** Where the marque is from. ISO 3166-1 alpha-2, lowercased. */
export const ORIGINS = ['it', 'de', 'jp', 'us', 'gb'];

/**
 * The three ribbons of the brand sheet's PWA panel, and a fourth above them. A
 * rarity is a claim about how many were made and how hard one is to find today —
 * content, and STILL not a game mechanic. SPEC-010 gave the product cars to
 * unlock and deliberately did not hang them on this: a car is a prize because of
 * where it was authored, not because of how many were built, so a corrected
 * production figure cannot silently relock a card somebody had won.
 *
 * `unicorn` is the fourth, and it is the first member of this vocabulary that is
 * not a judgement (SPEC-016 D-2). The top three ribbons had been carrying two
 * different claims in the same word — "famous" and "almost nobody has one" — and
 * a pack whose rarest car is a twenty-nine-unit Yellowbird wearing the same
 * yellow as a thirteen-hundred-unit F40 was making both claims at once. So the
 * new tier is defined by a NUMBER, `UNICORN_MAX_UNITS`, and `validateCar.js`
 * holds records to it in both directions.
 */
export const RARITIES = ['common', 'rare', 'legendary', 'unicorn'];

/**
 * How few of a car there has to be for the chrome ribbon: FEWER THAN twenty.
 *
 * Exported rather than left in the validator because three places need the same
 * number — the refusal, the test that proves the refusal, and any future record
 * that has to be argued about — and a threshold with two spellings is a
 * threshold that has already drifted.
 *
 * Twenty-five was the better-LOOKING line and was rejected: it leaves a nine-car
 * gap down to the Yellowbird's twenty-nine where twenty leaves three to the
 * Callaway's twenty, but it is a number chosen to capture one specific car, and
 * a threshold chosen backwards from the cars it should include is taste wearing
 * a rule's clothes (SPEC-016 D-3).
 *
 * A car whose run is genuinely unsettled carries `productionUnits: null` and
 * therefore CANNOT take this ribbon. That is the intended reading rather than an
 * edge case: the top of a catalogue is the last place to accept "roughly a
 * handful".
 */
export const UNICORN_MAX_UNITS = 20;

/** Drivetrain layout. */
export const DRIVETRAINS = ['rwd', 'awd', 'fwd'];

/** How the engine breathes. `quad-turbo` is the EB110's four, and the ceiling. */
export const ASPIRATIONS = ['natural', 'turbo', 'twin-turbo', 'supercharged', 'quad-turbo'];

/**
 * Where the engine SITS — the first half of what the eighties spec sheets
 * printed as one line, "mid engine/rear drive". `DRIVETRAINS` above is the other
 * half, and the two are separate vocabularies rather than one `mid-rwd` value
 * because a combined set is a cross-product: nine spellings to cover the five
 * combinations this catalogue actually contains, with copy for four that would
 * never render.
 */
export const ENGINE_POSITIONS = ['front', 'mid', 'rear'];

/**
 * How the cylinders are arranged — the "V-8" of "twin turbo dohc 32v V-8".
 *
 * `rotary` is a member rather than a null because the RX-7's engine has an
 * arrangement; what it does not have is cylinders and valves, which is why
 * `cylinders` and `valvetrain` are the fields nulled on that record. A rotary
 * that had to leave this field empty would draw an em dash under "Cylinders"
 * for the one car in the catalogue whose engine is the reason anybody remembers
 * it.
 */
export const ENGINE_CONFIGURATIONS = [
    'inline-4',
    'inline-6',
    'flat-6',
    'flat-12',
    'v6',
    'v8',
    'v10',
    'v12',
    // The Cizeta's, and the only one ever sold in a road car: two flat-plane
    // straight eights sharing a block, with the gearbox driven off the middle.
    'v16',
    'rotary',
];

/**
 * Cams and valve count — the "dohc 32v"; `dohc-60v` is the five-valve twelve.
 *
 * NULLABLE, and the only vocabulary here that is: a Wankel has neither a
 * camshaft nor a valve, so the RX-7 states `null` and its row draws an em dash.
 * That is a different answer from a record that forgot the field, which is why
 * the content test insists on `null` out loud rather than accepting `undefined`.
 */
export const VALVETRAINS = [
    // Ordered by cam layout and then by valve count, which is how a spec sheet
    // reads them rather than how a sort would.
    'ohv-16v',
    'ohv-20v',
    'sohc-12v',
    'sohc-24v',
    'dohc-16v',
    'dohc-24v',
    'dohc-24v-vtec',
    'dohc-32v',
    'dohc-48v',
    'dohc-60v',
    'dohc-64v',
];

/**
 * What kind of gearbox. Two members, and the second arrived exactly as the
 * first version of this comment said it would.
 *
 * It read: "Every car in this catalogue is a manual, and that is not an accident
 * of the selection — an automatic in this company would be a different product.
 * A speculative `automatic` sitting here would be copy nothing renders and a
 * branch no test can reach; the day a fifteenth car needs it, the value and its
 * message key arrive together."
 *
 * That day is the Vector W8 (SPEC-016 D-13): a 625 hp supercar on a three-speed
 * GM Turbo-Hydramatic, which is the single strangest line on its spec sheet and
 * precisely the sort of thing this catalogue exists to print. The value and
 * `cars.transmission.automatic` landed in one commit, and no record was quietly
 * relabelled to avoid admitting it.
 *
 * The rule the old comment was really stating still holds, and is worth keeping:
 * a member is added when a car needs it, never in advance.
 */
export const TRANSMISSION_TYPES = ['manual', 'automatic'];

/**
 * The livery washes — the dark cast a card's plate takes from the car's own
 * paint. One of the `--livery-*` tokens, without the prefix. Content names one;
 * the catalogue never invents a hex (see `tokens.css`).
 */
export const LIVERIES = [
    'red',
    'yellow',
    'silver',
    'white',
    'black',
    'blue',
    // The Diablo's artwork is a metallic violet, and it was carried as `blue`
    // until the plate was checked against the picture. Worth its own entry
    // rather than rounding to the nearest existing one: a purple car on a blue
    // plate is the exact failure the livery tokens exist to prevent.
    'purple',
    'green',
    'orange',
];

/**
 * How a famous person is attached to a car (SPEC-008).
 *
 * Six values, and the pair that matters most is `wheel` against `screen`.
 * "Drove it for real" and "Drove it on screen" are the same sentence in
 * ordinary speech and two completely different claims about a person: Phil Hill
 * took a Yellowbird to 211 mph at Ehra-Lessien, and Vin Diesel drove an RX-7
 * through a film set. A single `drove` value would flatten those together, and
 * flattening them is how this band would manufacture exactly the rumour it
 * exists to avoid repeating.
 *
 * Closed for the reason every vocabulary in this file is closed: the value
 * reaches the screen as `cars.owners.connection.${value}`, so a pack that
 * invented `racer` would print a raw message key on a chip beside a real,
 * frequently living person's name.
 */
export const OWNER_CONNECTIONS = [
    'owner',
    'wheel',
    'screen',
    'development',
    'designer',
    'commissioner',
];

/**
 * How many people a car's band may name.
 *
 * Three is the target and the ceiling; ONE is the floor, because famous
 * ownership is the most rumour-prone content in this catalogue and a car whose
 * third name cannot be sourced has to be allowed to ship two. That is SPEC-002
 * D-29 for people — the Yellowbird states `market: null` rather than quoting one
 * of three extraordinary hammer prices, and for the same reason a car here gets
 * a shorter band rather than a plausible fourth name.
 */
export const OWNERS_PER_CAR_MAX = 3;

/**
 * The four figures that go on the hero plate, in the order the brand sheet's
 * §12 draws them. They are the specs a fourteen-year-old could recite, which is
 * the entire selection rule: everything else lives further down the card.
 */
export const HEADLINE_SPECS = ['topSpeed', 'power', 'zeroToHundred', 'weight'];

/**
 * Which picture of a car to draw, re-exported from `carTakes.js`.
 *
 * The take rules moved out at the 350-line rule; this file stays their single
 * address. Two reasons it is a re-export rather than a second import path every
 * caller has to learn: `scripts/lib/carSchema.js` loads THIS module and hands
 * the build scripts one schema object, and a vocabulary that had to be fetched
 * from two places is the "second spelling" this file's own comments warn about
 * everywhere else.
 *
 * `./carTakes.js` WITH the extension, which is the one import in `src/` that
 * carries one. Everywhere else a bundler resolves the specifier and an
 * extensionless path is the house style; this module is the single file Node
 * loads directly — `scripts/lib/carSchema.js` requires it so the build scripts
 * validate against the real schema rather than a mirror — and Node's ESM
 * resolver does not guess extensions. Without it every content and asset build
 * dies on a module it can see on disk.
 */
export * from './carTakes.js';

/**
 * The standing distances a trace point may be, re-exported from
 * `carTraceMarks.js` for the same reason and on the same terms as the takes
 * above — including the extension, which Node's ESM resolver does not guess.
 *
 * NOT a member of `VOCABULARIES`. That map is walked field by field against a
 * CAR record, and a mark is a property of a POINT inside one; adding it there
 * would make `validateVocabularies` go looking for `car.traceMark` and refuse
 * every record in the pack. `validateCar.js` is handed `TRACE_MARKS` directly.
 */
export * from './carTraceMarks.js';

const VOCABULARIES = {
    decade: DECADES,
    class: CLASSES,
    origin: ORIGINS,
    rarity: RARITIES,
    drivetrain: DRIVETRAINS,
    aspiration: ASPIRATIONS,
    enginePosition: ENGINE_POSITIONS,
    engineConfiguration: ENGINE_CONFIGURATIONS,
    valvetrain: VALVETRAINS,
    transmissionType: TRANSMISSION_TYPES,
    livery: LIVERIES,
    connection: OWNER_CONNECTIONS,
};

/**
 * The vocabularies that describe somebody ON a car rather than the car itself.
 *
 * `connection` is a property of a PERSON on the famous-owners band, not a
 * property of the machine — an F40 has a livery and a class, and Nigel Mansell
 * has a connection to it. They share `VOCABULARIES` because they share
 * `isKnown`, and one closed-set answer is what both the runtime join and the
 * build validator need.
 *
 * Named here rather than left implicit because two suites walk `VOCABULARIES`
 * asserting that every closed set is checked against the car records, and both
 * would otherwise fail the moment this one was added — with a message saying the
 * schema had grown a field the content pack forgot, which is the opposite of
 * what happened. Subtracting a list the schema itself publishes keeps that guard
 * honest: a genuinely new CAR vocabulary still has to be accounted for.
 *
 * Covered instead by `scripts/content/carOwners.test.js` and
 * `src/domain/cars/carOwners.test.js`, which walk the owner records.
 */
export const OWNER_VOCABULARIES = ['connection'];

/**
 * The vocabularies a record is allowed to answer `null` to.
 *
 * Exported rather than left as a rule each caller restates, because two of them
 * check it — the build's validator and the content test — and a vocabulary that
 * was nullable in one and required in the other would let a record through in
 * exactly one direction. `null` here means the catalogue is saying the field
 * does not APPLY, which is a different claim from a missing field and renders as
 * the same em dash; see the RX-7.
 */
export const NULLABLE_VOCABULARIES = ['valvetrain'];

/**
 * Is `value` in the closed set `field` names?
 *
 * Exported rather than inlined because both the runtime catalogue and the build
 * script that writes it have to answer the same question, and two spellings of
 * a vocabulary is how a content pack quietly ships a value nothing renders.
 */
export function isKnown(field, value) {
    const vocabulary = VOCABULARIES[field];

    if (!vocabulary) {
        throw new Error(`No car vocabulary called ${field}`);
    }

    return vocabulary.includes(value);
}

export { VOCABULARIES };
